import { useState } from 'react';
import {
  Box, Button, Stack, TextField, MenuItem, Typography, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Calculate as CalcIcon, Save as SaveIcon } from '@mui/icons-material';
import { useBeneficiosStore } from '../store/beneficiosStore';

const currentYear = new Date().getFullYear();

export default function CTSPanel() {
  const { ctsResultados, depositosCTS, loading, calcularCTS, grabarCTS, loadDepositosCTS } = useBeneficiosStore();
  const [semestre, setSemestre] = useState(1);
  const [anio, setAnio] = useState(currentYear);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCalcular = () => calcularCTS(semestre, anio);
  const handleGrabar = () => grabarCTS(semestre, anio, 0);
  const handleLoadDepositos = () => loadDepositosCTS(anio);

  const calculoColumns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 150 },
    ...(!isMobile ? [
      { field: 'remuneracionComputable', headerName: 'Rem. Comp.', width: 130, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'sextoGratificacion', headerName: '1/6 Grat.', width: 110, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'asignacionFamiliar', headerName: 'Asig. Fam.', width: 110, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'mesesComputables', headerName: 'Meses', width: 70, type: 'number' as const },
      { field: 'diasComputables', headerName: 'Días', width: 70, type: 'number' as const },
    ] : []),
    { field: 'montoDeposito', headerName: 'Monto CTS', width: isMobile ? 110 : 130, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2),
      cellClassName: 'font-bold' },
  ];

  const depositoColumns: GridColDef[] = [
    { field: 'anio', headerName: 'Año', width: 70 },
    { field: 'semestre', headerName: 'Sem.', width: 60 },
    ...(!isMobile ? [
      { field: 'remuneracionComputable', headerName: 'Rem. Comp.', width: 130, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
    ] : []),
    { field: 'montoDeposito', headerName: 'Monto', width: 130, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2) },
    { field: 'estado', headerName: 'Estado', width: 110 },
    ...(!isMobile ? [
      { field: 'fechaCalculo', headerName: 'Fecha Cálculo', width: 130,
        valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
    ] : []),
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField
            select size="small" label="Semestre" value={semestre}
            onChange={(e) => setSemestre(Number(e.target.value))} sx={{ flex: { xs: 1, sm: 'none' }, width: { sm: 150 } }}
          >
            <MenuItem value={1}>Sem 1 (Nov-Abr)</MenuItem>
            <MenuItem value={2}>Sem 2 (May-Oct)</MenuItem>
          </TextField>
          <TextField
            size="small" label="Año" type="number" value={anio}
            onChange={(e) => setAnio(Number(e.target.value))} sx={{ width: 100 }}
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <CalcIcon />}
            onClick={handleCalcular} disabled={loading} size={isMobile ? 'small' : 'medium'}>
            Calcular CTS
          </Button>
          {ctsResultados.length > 0 && (
            <Button variant="outlined" color="success" startIcon={<SaveIcon />}
              onClick={handleGrabar} disabled={loading} size={isMobile ? 'small' : 'medium'}>
              Grabar
            </Button>
          )}
          <Button variant="text" onClick={handleLoadDepositos} size={isMobile ? 'small' : 'medium'}>
            Ver Depósitos
          </Button>
        </Stack>
      </Stack>

      {ctsResultados.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Cálculo CTS - Semestre {semestre} / {anio}
          </Typography>
          <DataGrid
            rows={ctsResultados}
            columns={calculoColumns}
            getRowId={(row) => row.idTrabajador}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Total: S/ {ctsResultados.reduce((s, r) => s + r.montoDeposito, 0).toFixed(2)} |
            Trabajadores: {ctsResultados.length}
          </Typography>
        </>
      )}

      {depositosCTS.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Depósitos CTS Grabados
          </Typography>
          <DataGrid
            rows={depositosCTS}
            columns={depositoColumns}
            getRowId={(row) => row.idDepositoCTS}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </Box>
      )}
    </Box>
  );
}
